import { watch, ComputedRef } from 'vue';
import { toDataPath, Resolve, JsonSchema } from '@jsonforms/core';
import { CommonJsonFormsControl } from './types';

/**
 * Resets one or more sibling fields to `undefined` whenever this control's own data changes.
 *
 * UISchema usage — changing "Location" clears the dependent Name/Environment/APMID fields
 * since they only make sense for the previously selected location:
 * ```json
 * {
 *   "type": "Control",
 *   "label": "Location",
 *   "scope": "#/properties/sourceSegment/properties/location",
 *   "options": {
 *     "clearOnRefresh": [
 *       "#/properties/sourceSegment/properties/name",
 *       "#/properties/sourceSegment/properties/environment",
 *       "#/properties/sourceApmId"
 *     ]
 *   }
 * }
 * ```
 */
export function useClearOnRefresh(
  control: ComputedRef<CommonJsonFormsControl>,
  handleChange: (path: string, value: unknown) => void,
): void {
  const clearOnRefresh = control.value.uischema?.options?.clearOnRefresh;
  // No clearOnRefresh configured for this control — nothing to watch, bail out early.
  if (!Array.isArray(clearOnRefresh) || clearOnRefresh.length === 0) return;

  watch(
    () => control.value.data,
    (newValue, oldValue) => {
      // Skips no-op invocations for primitives, but guard explicitly
      // in case the same value is re-emitted programmatically (e.g. after a reset).
      if (newValue === oldValue) return;

      // Skip when the change comes from programmatic hydration (e.g. pre-populating the form props.editData)
      if (control.value.config?.suppressClearOnRefresh) return;

      // This field's value changed — clear every dependent field listed in clearOnRefresh so
      // stale values (computed/entered relative to the *old* value) don't linger in formData.
      for (const scopePointer of clearOnRefresh) {
        const targetSchema = Resolve.schema(
          control.value.rootSchema as JsonSchema,
          scopePointer,
          control.value.rootSchema as JsonSchema,
        );
        const emptyValue = targetSchema?.type === 'array' ? [] : undefined;
        handleChange(toDataPath(scopePointer), emptyValue);
      }
    },
  );
}
